import React, { FC, useEffect, useMemo, useState } from 'react';
import { MainpageBrand } from './MainpageBrand';
import { useAdfoxContext } from '../../../external/context/AdfoxContext';
import { declineNumeric, formatPrice } from '../../../../../utils/format_helper';
import { sanitize } from 'isomorphic-dompurify';
import { useApplicationContext } from '../../../../../utils/applicationContext';
import { useBackgroundContext } from '../../../../BackgroundProvider';

export type TMainpageBrandContext = {
  type: 'mainpageBrand';
  bgImageUrl: string;
  logoImageUrl: string;
  name: string;
  actionText: string;
  newBuildingId?: string;
  phone: string;
  url: string;
  buttonText: string;
  info: string;
  substrateNotNeeded?: string;
  isApartment?: string;
  note?: string;
  label?: string;
  mortgageWarning?: string;
  mortgageTermsWarning?: string;
};

type MainpageBrandContainerProps = {
  context: TMainpageBrandContext;
};

interface IHousingInfo {
  name: string;
  min_price2: number;
  min_price2_square?: number;
  offers_count: number;
  address: string;
  builders: string[];
  isFetching: boolean;
}

export const MainpageBrandContainer: FC<MainpageBrandContainerProps> = ({ context }) => {
  const { makeRequest, logger } = useApplicationContext();
  const { sizes } = useAdfoxContext();
  const [, setBackgroundImg] = useBackgroundContext();

  const {
    mortgageTermsWarning,
    mortgageWarning,
    name,
    phone,
    actionText,
    info,
    logoImageUrl,
    label,
    substrateNotNeeded,
    url,
    newBuildingId,
    isApartment,
    buttonText = 'Узнать цены',
    note,
    bgImageUrl,
  } = context;

  const [housingInfo, setHousingInfo] = useState<IHousingInfo | null>(null);

  const sanitizedName = useMemo(() => name && sanitize(name), [name]);
  const sanitizedPhone = useMemo(() => phone && sanitize(phone), [phone]);
  const sanitizedActionText = useMemo(() => actionText && sanitize(actionText), [actionText]);
  const sanitizedInfo = useMemo(() => {
    let bannerInfoText: string | undefined = info;

    if (housingInfo && housingInfo.offers_count) {
      bannerInfoText = `В продаже ${formatPrice(housingInfo.offers_count)} ${declineNumeric(
        housingInfo.offers_count,
        isApartment === '1' ? ['апартамент', 'апартамента', 'апартаментов'] : ['квартира', 'квартиры', 'квартир'],
      )} по цене от ${formatPrice(housingInfo.min_price2)} ₽`;
    }

    return bannerInfoText && sanitize(bannerInfoText);
  }, [housingInfo, info, isApartment]);

  useEffect(() => {
    if (newBuildingId) {
      /**
       * @todo Стандартизировать запрос
       * @description Данный функционал появился в задаче CD-236003, будет удалён в задаче CD-241103
       * На данный момент запрос выполняется через легаси способ, через fetch не работает,
       * нужно сознавать нормальный эндпоинт с понятным и хорошим контрактом, выносить логику на бекенд по формированию строк,
       * то есть получать уже готовые строки, а не формировать их на клиенте. Контракт по обогащению баннера на запрос
       * тоже нужно привести к единому виду, смотри обогащение данных для бренд баннеров
       */
      makeRequest({
        method: 'GET',
        uri: {
          host: null,
          path: '/ajax/sections_housing_info/',
          query: `id=${newBuildingId}`,
        },
      }).then(
        (res: { data: IHousingInfo }) => setHousingInfo(res.data),
        () =>
          logger.error('Failed to fetch housing info', {
            newBuildingId,
            domain: 'src/shared/components/AdfoxBanner/internal/components/MainpageBrand/MainpageBrandContainer.tsx',
          }),
      );
    }

    if (bgImageUrl) {
      setBackgroundImg(bgImageUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainpageBrand
      label={label}
      url={url}
      substrateNotNeeded={substrateNotNeeded}
      logoImageUrl={logoImageUrl}
      sizes={sizes}
      buttonText={buttonText}
      sanitizedActionText={sanitizedActionText}
      sanitizedName={sanitizedName}
      sanitizedPhone={sanitizedPhone}
      sanitizedInfo={sanitizedInfo}
      mortgageTermsWarning={mortgageTermsWarning}
      mortgageWarning={mortgageWarning}
      note={note}
    />
  );
};
